import { createApp, watch } from "vue";
import App from "./App.vue";
import router, { checkMaintenance } from "@/router/index";
import i18n, { loadLocaleMessages } from "./i18n";
import store from "@/utils/store";
import imageHelper from "@/utils/imageHelper";
import consoleController from "@/utils/logUtil";
import constant from "@/const";

const app = createApp(App);

/**
 * ハッシュモードのURLからクエリパラメータを取得
 */
function getQueryParamsFromHash() {
  const { hash } = window.location; // #/path?param=value

  // クエリパラメータ部分を抽出
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) {
    return new URLSearchParams();
  }

  const queryString = hash.substring(queryIndex + 1);
  return new URLSearchParams(queryString);
}

/**
 * アプリ起動
 */
async function initializeApp() {
  const hashParams = getQueryParamsFromHash();
  // URLのクエリパラメータから言語を初期化
  const country = hashParams.get("country");
  const lang = hashParams.get("lang");
  store.initLocaleFromQuery(country, lang);

  // 指定された言語ファイルを読み込み
  try {
    await loadLocaleMessages(store.currentLocale.value);
    consoleController("log", `Initial language: ${store.currentLocale.value}`);
  } catch (error) {
    consoleController(
      "error",
      `failed to load initial language: ${store.currentLocale.value}`,
    );
    consoleController("error", error);
  }
  // vue-i18nとstore.jsを同期
  i18n.global.locale = store.currentLocale.value;

  // 言語変更を監視してvue-i18nと同期
  watch(store.currentLocale, async (newLocale) => {
    consoleController("log", `locale changed: ${newLocale}`);
    // 新しい言語ファイルを読み込み
    await loadLocaleMessages(newLocale);

    // i18nのロケールを変更
    i18n.global.locale = newLocale;

    // URLパラメータを変更
    const currentRoute = router.currentRoute.value;
    const newQuery = { ...currentRoute.query };
    // 地域コードがデフォルト値の場合、クエリから取り除く
    if (newLocale === constant.DEFAULT_LOCALE) {
      delete newQuery.lang;
    }
    // URLに含める言語コード
    const urlLang = store.getUrlLang(newLocale);
    if (urlLang !== "") {
      newQuery.lang = urlLang;
    }
    // 言語が変わっていたら実行
    if (currentRoute.query.lang !== newQuery.lang) {
      await router.replace({
        path: currentRoute.path,
        query: newQuery,
      });
    }
  });

  // ========================================
  // グローバルプロパティとして画像関数を登録
  // ========================================

  /**
   * 共通画像を取得
   * 使用例: this.$getCommonImage('log', 'svg')
   */
  app.config.globalProperties.$getCommonImage = (imageName, ext = "png") =>
    imageHelper.getCommonImage(imageName, ext);

  /**
   * 多言語画像を非同期で取得
   * 使用例: await this.$loadI18nImage('main', 'png')
   */
  app.config.globalProperties.$loadI18nImage = function $loadI18nImage(
    imageName,
    locale = null,
    ext = "png",
  ) {
    const url = imageHelper.getLocalizedImageAsync(
      imageName,
      locale || store.currentLocale.value,
      ext,
    );

    // コンポーネントのdataに画像URLを保存
    const key = `i18n_${imageName}_${ext}`;
    if (this && this.$data) {
      this[key] = url;
    }

    return url;
  };

  /**
   * 読み込み済みの多言語画像を取得
   * 使用例: this.$getI18nImage('main', 'png')
   */
  app.config.globalProperties.$getI18nImage = function $getI18nImage(
    imageName,
    ext = "png",
  ) {
    const key = `i18n_${imageName}_${ext}`;
    return this[key] || "";
  };

  /**
   * 現在の地域コードを取得
   * 使用例: this.$currentLocale
   */
  Object.defineProperty(app.config.globalProperties, "$currentLocale", {
    get() {
      return store.currentLocale.value;
    },
  });

  // URLに含める言語コード
  const urlLang = store.getUrlLang(store.currentLocale.value);
  await checkMaintenance(urlLang);

  app.use(router);
  app.use(i18n);

  // Vue Routerの準備が整うのを待つ
  await router.isReady();

  // URLのcountryとlangを判定結果と同期する
  const newQuery = { ...router.currentRoute.value.query };

  // 国コードがデフォルト値の場合、クエリから取り除く
  if (store.currentCountry === constant.DEFAULT_COUNTRY) {
    delete newQuery.country;
  } else {
    newQuery.country = store.currentCountry.value;
  }
  // 地域コードがデフォルト値の場合、クエリから取り除く
  if (store.currentLocale.value === constant.DEFAULT_LOCALE) {
    delete newQuery.lang;
  }
  // URLに含める言語コード
  if (urlLang !== "") {
    newQuery.lang = urlLang;
  }

  await router.replace({
    path: router.currentRoute.value.path,
    query: newQuery,
  });

  app.mount("#app");
}

// アプリケーション初期化
initializeApp();
